import { useEffect, useState } from 'react'
import { Capacitor } from '@capacitor/core'

/*
 * The desktop shell: a top navigation bar instead of the phone's bottom tabs.
 *
 * "Desktop" means a wide browser window, never the native app: inside the iOS
 * app the layout is the phone's at every size, so an iPad in the app keeps the
 * bottom tabs and one column.
 *
 * The bar and the screens it switches between live in different components, so
 * they talk through three window events rather than a prop chain through App:
 *   GOTO   the bar asks the screens to show a tab
 *   TAB    the screens say which tab is showing now (for the bar's highlight)
 *   COUNTS the screens say what is waiting (unread messages, new requests)
 */
export const DESKTOP_QUERY = '(min-width: 1100px)'
export const GOTO = 'worx:goto'
export const TAB = 'worx:tab'
export const COUNTS = 'worx:counts'

export interface GotoDetail { tab: string }
export interface TabDetail { side: string; tab: string }
export interface CountsDetail { unread: number; requests: number }

export function emit<T>(name: string, detail: T): void {
  window.dispatchEvent(new CustomEvent<T>(name, { detail }))
}

export function useDesktop(): boolean {
  const native = Capacitor.isNativePlatform()
  const [wide, setWide] = useState(() => !native && window.matchMedia(DESKTOP_QUERY).matches)
  useEffect(() => {
    if (native) return
    const query = window.matchMedia(DESKTOP_QUERY)
    const on = () => setWide(query.matches)
    on()
    query.addEventListener('change', on)
    return () => query.removeEventListener('change', on)
  }, [native])
  return wide && !native
}
